import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ActionButtonGroup, Responsive, SettingsButtons, TabContainer } from '@components';
import { FormActionType, FormType, SecurityTab, Translation } from '@enums';
import {
  useFetchFormList,
  useFormHandlers,
  useFormLoadingStatus,
  useFormProcedure,
  useFormSubmissionStatus,
} from '@hooks';
import { getSecurityTabs, validateValueEqual } from '@utils';

import { SecurityForm } from './securityForm';

export const Security = () => {
  const { id: application_id } = useParams();

  const formType = FormType.CdnSecurity;

  const { t: tConfigurationButton } = useTranslation(Translation.Configuration, { keyPrefix: 'buttons' });

  const formikRef = useRef<FormikProps<any> | null>(null);
  const [activeTab, setActiveTab] = useState(SecurityTab.SECURITY);

  const formProcedure = useFormProcedure(formType);
  const formSubmitting = useFormSubmissionStatus({ formType });
  const formLoading = useFormLoadingStatus({ formType });

  useFetchFormList({
    formType,
    params: { application_id },
    globalOptions: {
      content: true,
    },
  });

  const handleSaveSettings = useCallback(() => {
    formikRef?.current?.handleSubmit();
  }, []);

  const handleFormikInstance = useCallback((formik: FormikProps<any> | null) => {
    formikRef.current = formik;
  }, []);

  const SecurityFormHandler = useFormHandlers({
    formType,
    handlerOptions: [
      {
        type: FormActionType.Save,
        function: handleSaveSettings,
        label: tConfigurationButton('saveSettings'),
        loading: formSubmitting,
        disabled: formLoading,
      },
    ],
  });

  const contentConfig = useCallback(() => {
    return {
      [SecurityTab.SECURITY]: <SecurityForm onFormikInstance={handleFormikInstance} formikRef={formikRef} />,
    };
  }, []);

  const SecurityTabs = useMemo(() => getSecurityTabs(), [tConfigurationButton]);

  return (
    <div className="w-full flex flex-col flex-1 gap-6 sm:gap-8">
      <TabContainer<SecurityTab>
        tabs={SecurityTabs}
        onTabChange={setActiveTab}
        contentConfig={contentConfig()}
        contentClassName="gap-6 sm:gap-8"
        adornment={
          <Responsive showAbove="sm">
            {validateValueEqual(activeTab, SecurityTab.SECURITY) && (
              <ActionButtonGroup actionHandlers={SecurityFormHandler} procedure={formProcedure} />
            )}
          </Responsive>
        }
      />
      <Responsive showBelow="sm">
        {validateValueEqual(activeTab, SecurityTab.SECURITY) && (
          <SettingsButtons handleSaveModal={handleSaveSettings} formType={formType} />
        )}
      </Responsive>
    </div>
  );
};
